import React, { useState } from "react";
import api from "@/lib/api";
import { mediaUrl } from "@/lib/upload";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileArrowDown, ArrowSquareOut, FilePdf, FileZip, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const DOC_LABELS = [
  ["photo", "Passport Photo"], ["id_proof", "ID / Passport"], ["address_proof", "Address Proof"],
  ["edu_proof", "Education Proof"], ["bank_info", "Bank Info"], ["cv", "CV / Resume"],
];
const PERSONAL = [["name", "Full Name"], ["dob", "Date of Birth"], ["phone", "Phone"], ["blood_group", "Blood Group"], ["address", "Address"], ["cloth_size", "Cloth Size"], ["shoe_size", "Shoe Size"], ["health_issues", "Health Issues"]];
const FAMILY = [["father_name", "Father's Name"], ["father_phone", "Father's Phone"], ["mother_name", "Mother's Name"], ["mother_phone", "Mother's Phone"], ["friend_name", "Friend's Name"], ["friend_phone", "Friend's Phone"], ["emergency_contact", "Emergency Contact"]];

function Section({ title, rows, data }) {
  return (
    <div>
      <h4 className="text-[11px] font-bold uppercase tracking-wide text-blue-600 mb-2">{title}</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 rounded-xl border border-slate-200 p-4">
        {rows.map(([k, label]) => (
          <div key={k} className={k === "address" || k === "health_issues" ? "sm:col-span-2" : ""}>
            <p className="text-[11px] font-semibold text-slate-400">{label}</p>
            <p className="text-sm text-slate-900 break-words" data-testid={`hr-view-${k}`}>{data[k] || "—"}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function DocCard({ label, url }) {
  const full = mediaUrl(url);
  const [imgOk, setImgOk] = useState(true);
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden flex flex-col">
      <div className="h-28 bg-slate-50 grid place-items-center overflow-hidden">
        {imgOk ? <img src={full} alt={label} className="w-full h-full object-cover" onError={() => setImgOk(false)} /> : <FilePdf size={36} weight="duotone" className="text-blue-600" />}
      </div>
      <div className="p-2.5 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-slate-800 truncate">{label}</p>
        <div className="flex gap-1 shrink-0">
          <a href={full} target="_blank" rel="noreferrer" className="p-1.5 text-slate-400 hover:text-blue-600" title="Open"><ArrowSquareOut size={15} /></a>
          <a href={full} download className="p-1.5 text-slate-400 hover:text-blue-600" title="Download"><FileArrowDown size={15} /></a>
        </div>
      </div>
    </div>
  );
}

export function OnboardingDetails({ invite, onClose }) {
  const [zipping, setZipping] = useState(false);
  if (!invite) return null;
  const data = invite.onboarding || {};
  const docs = data.documents || {};
  const uploaded = DOC_LABELS.filter(([k]) => docs[k]);

  const downloadZip = async () => {
    setZipping(true);
    try {
      const { data: blob } = await api.get(`/hr/onboarding/${invite.id}/documents.zip`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([blob], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${(invite.name || "employee").replace(/[^A-Za-z0-9]+/g, "-")}-onboarding-documents.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error("Could not download documents"); }
    finally { setZipping(false); }
  };

  return (
    <Dialog open={!!invite} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-slate-200 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="hr-onboarding-details">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">{data.name || invite.name}<CheckCircle size={18} weight="fill" className="text-emerald-500" /></DialogTitle>
          <DialogDescription className="text-slate-500">{invite.email} · <span className="capitalize">{invite.role}</span>{invite.submitted_at ? ` · Submitted ${new Date(invite.submitted_at).toLocaleDateString()}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-1">
          <Section title="Personal" rows={PERSONAL} data={data} />
          <Section title="Family & Emergency" rows={FAMILY} data={data} />
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[11px] font-bold uppercase tracking-wide text-blue-600">Documents ({uploaded.length}/{DOC_LABELS.length})</h4>
              <Button onClick={downloadZip} disabled={zipping || uploaded.length === 0} className="h-8 bg-blue-600 text-white hover:bg-blue-700 text-xs font-semibold" data-testid="hr-download-zip">
                <FileZip size={15} className="mr-1.5" />{zipping ? "Preparing…" : "Download All (ZIP)"}
              </Button>
            </div>
            {uploaded.length === 0 ? <p className="text-sm text-slate-400 rounded-xl border border-dashed border-slate-200 p-4">No documents were uploaded.</p> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="hr-docs-grid">
                {uploaded.map(([k, label]) => <DocCard key={k} label={label} url={docs[k]} />)}
              </div>
            )}
            {DOC_LABELS.length > uploaded.length && uploaded.length > 0 && (
              <p className="text-xs text-slate-400 mt-2">Missing: {DOC_LABELS.filter(([k]) => !docs[k]).map(([, l]) => l).join(", ")}</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
